import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useWebSocket } from '../context/WebSocketContext'
import {
  Home,
  TrendingUp,
  Bell,
  Send,
  User,
  LogOut,
  CreditCard,
  Menu,
  X,
  Settings,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useEffect, useState } from 'react'
import { useAppConfig } from '../context/AppConfigContext'

const MOBILE_BREAKPOINT = 768

const Navbar = () => {
  const { user, logout } = useAuth()
  const { showBanner } = useAppConfig()
  // When banner is showing, navbar shifts down by banner height (36px = top-9)
  const navTop = showBanner ? 'top-9' : 'top-0'
  const { unreadCount } = useWebSocket()
  const navigate = useNavigate()
  const location = useLocation()

  const [isMobile, setIsMobile] = useState(
    window.innerWidth < MOBILE_BREAKPOINT
  )
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT
      setIsMobile(mobile)
      if (!mobile) setMenuOpen(false)
    }

    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const handleLogout = () => {
    logout()
    setMenuOpen(false)
    navigate('/')
  }

  const isActive = (path) => location.pathname === path

  const NavItem = ({ to, icon: Icon, label }) => (
    <Link to={to} onClick={() => setMenuOpen(false)}>
      <Button
        variant={isActive(to) ? 'default' : 'ghost'}
        className="w-full justify-start"
      >
        <Icon className="w-5 h-5 mr-3" />
        {label}
      </Button>
    </Link>
  )

  // ---------------- ADMIN NAV (unchanged behavior)
  if (user && (user.role === 'admin' || user.role === 'super_admin')) {
    return (
      <nav className={`fixed ${navTop} left-0 right-0 z-50 bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border-b border-gray-200 dark:border-slate-700 transition-all`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link
              to={user.role === 'super_admin' ? '/super-admin' : '/admin'}
              className="text-2xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent"
            >
              {isMobile ? '🌀' : 'LitX'}
            </Link>
            {user?.role === 'super_admin' && (
              <Link to="/control-panel">
                <Button variant={isActive('/control-panel') ? 'default' : 'ghost'} size="sm" className={isActive('/control-panel') ? '' : 'text-gray-700 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white'}>
                  <Settings className="w-4 h-4 mr-2" />
                  Control Panel
                </Button>
              </Link>
            )}
            <Button onClick={handleLogout} variant="ghost" size="sm" className="text-gray-700 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-400">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </nav>
    )
  }

  // ---------------- MOBILE NAV
  if (isMobile) {
    return (
      <>
        <nav className={`fixed ${navTop} left-0 right-0 z-50 bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border-b border-gray-200 dark:border-slate-700 transition-all`}>
          <div className="flex items-center justify-between h-16 px-4">
            <Link
              to={user ? '/home' : '/'}
              className="text-2xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent"
            >
              🌀
            </Link>

            <Button variant="ghost" aria-label="Open menu" onClick={() => setMenuOpen(true)} className="text-gray-700 dark:text-slate-200">
              <Menu className="w-6 h-6" />
            </Button>
          </div>
        </nav>

        {menuOpen && (
          <div className="fixed inset-0 z-50 bg-black/40">
            <div className="absolute top-0 right-0 w-72 h-full bg-white dark:bg-slate-900 dark:border-l dark:border-slate-700 shadow-xl p-4 flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <span className="font-semibold text-lg text-gray-900 dark:text-white">Menu</span>
                <Button variant="ghost" onClick={() => setMenuOpen(false)} className="text-gray-700 dark:text-slate-200">
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="flex-1 space-y-1">
                <NavItem to="/home" icon={Home} label="Home" />
                <NavItem to="/trending" icon={TrendingUp} label="Trending" />
                {user && (
                  <>
                    <Link to="/notifications" onClick={() => setMenuOpen(false)}>
                      <Button
                        variant={isActive('/notifications') ? 'default' : 'ghost'}
                        className="w-full justify-start relative"
                      >
                        <Bell className="w-5 h-5 mr-3" />
                        Notifications
                        {unreadCount > 0 && (
                          <span className="ml-auto text-xs font-bold bg-cyan-500 text-white rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center">
                            {unreadCount > 99 ? '99+' : unreadCount}
                          </span>
                        )}
                      </Button>
                    </Link>
                    <NavItem to="/submit" icon={Send} label="Submit" />
                    <NavItem to="/profile" icon={User} label="Profile" />
                    <NavItem to="/payment" icon={CreditCard} label="Payment" />
                  </>
                )}
              </div>

              {user && (
                <Button
                  variant="ghost"
                  className="justify-start mt-4 text-gray-700 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-400"
                  onClick={handleLogout}
                >
                  <LogOut className="w-5 h-5 mr-3" />
                  Logout
                </Button>
              )}
            </div>
          </div>
        )}
      </>
    )
  }

  // ---------------- DESKTOP NAV (unchanged layout)
  return (
    <nav className={`fixed ${navTop} left-0 right-0 z-50 bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border-b border-gray-200 dark:border-slate-700 transition-all`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link
            to={user ? '/home' : '/'}
            className="text-xl sm:text-2xl font-bold whitespace-nowrap shrink-0 bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent"
          >
            LitX
          </Link>

          <div className="flex items-center space-x-1 flex-shrink-0">
            <Link to="/home">
              <Button variant={isActive('/home') ? 'default' : 'ghost'} size="sm" className={isActive('/home') ? '' : 'text-gray-700 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white'}>
                <Home className="w-4 h-4 mr-2" />
                Home
              </Button>
            </Link>

            <Link to="/trending">
              <Button
                variant={isActive('/trending') ? 'default' : 'ghost'}
                size="sm"
                className={isActive('/trending') ? '' : 'text-gray-700 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white'}
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Trending
              </Button>
            </Link>

            {user && (
              <>
                <Link to="/notifications">
                  <Button
                    variant={isActive('/notifications') ? 'default' : 'ghost'}
                    size="sm"
                    className={isActive('/notifications') ? 'relative' : 'relative text-gray-700 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white'}
                  >
                    <Bell className="w-4 h-4 mr-2" />
                    Notifications
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 text-xs font-bold bg-cyan-500 text-white rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center leading-none">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </Button>
                </Link>

                <Link to="/submit">
                  <Button
                    variant={isActive('/submit') ? 'default' : 'ghost'}
                    size="sm"
                    className={isActive('/submit') ? '' : 'text-gray-700 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white'}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Submit
                  </Button>
                </Link>

                <Link to="/profile">
                  <Button
                    variant={isActive('/profile') ? 'default' : 'ghost'}
                    size="sm"
                    className={isActive('/profile') ? '' : 'text-gray-700 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white'}
                  >
                    <User className="w-4 h-4 mr-2" />
                    Profile
                  </Button>
                </Link>

                <Link to="/payment">
                  <Button
                    variant={isActive('/payment') ? 'default' : 'ghost'}
                    size="sm"
                    className={isActive('/payment') ? '' : 'text-gray-700 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white'}
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Payment
                  </Button>
                </Link>

                <Button onClick={handleLogout} variant="ghost" size="sm" className="text-gray-700 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-400">
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  )
}

export default Navbar